import React, { useState, useContext, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button, Row, Col, Tooltip } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Formik, Form, Field } from 'formik';
import { DateTime } from "luxon";
import { gql, useQuery, useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import Swal from 'sweetalert2';

import { ReactDatePicker, ReactSwitch, TimePicker, Input } from '../helpers/formik';
import getEncodedImageUrl from '../helpers/get-encoded-image';
import { BackArrow } from './svgs'
import { PreLoader } from './preloader';
import { Presentation } from '../types/presentation';
import { useHistory } from "react-router-dom";
import EventContext from '../contexts/event';
import axios, { AxiosResponse } from "axios";
import toast, { Toaster } from 'react-hot-toast';

import { secondsToTime } from '../helpers';
import {
  buildSettingsFormFromPresentation,
  hasQuizModeratorSettingsChanges,
  QuizModeratorSettingsForm,
  SidebarQuizSettingsHandle,
} from '../helpers/quiz-moderator-settings';
import { buildQuizScheduleTimestamp } from '../helpers/quiz-schedule-time';

const presentation_by_pk = gql`
  query presentation_by_pk($id: String!) {
    presentation_by_pk(id: $id) {
      id
      live
      questions_from_time
      questions_to_time
      results_from_time
      results_to_time
      timer
    }
  }
`

const update_presentation_by_pk = gql`
  mutation update_presentation_by_pk(
    $pk_columns: presentation_pk_columns_input!
    $live: Boolean
    $questions_from_time: timestamptz
    $questions_to_time: timestamptz
    $results_from_time: timestamptz
    $results_to_time: timestamptz
    $timer:Int
  ) {
    update_presentation_by_pk(
      pk_columns: $pk_columns,
      _set: {
        live: $live
        questions_from_time: $questions_from_time
        questions_to_time: $questions_to_time
        results_from_time: $results_from_time
        results_to_time: $results_to_time
        timer:$timer
      }
    ) {
      id
      questions_from_time
      questions_to_time
      results_from_time
      results_to_time
      live
      timer
    }
  }
`

const SettingsDirtyTracker: React.FC<{
  values: QuizModeratorSettingsForm;
  baseline: QuizModeratorSettingsForm | null;
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ values, baseline, onDirtyChange }) => {
  useEffect(() => {
    if (!baseline) {
      return;
    }
    onDirtyChange?.(hasQuizModeratorSettingsChanges(values, baseline));
  }, [values, baseline, onDirtyChange]);

  return null;
};

const SidebarQuizModerator = forwardRef<
  SidebarQuizSettingsHandle,
  {
    addQuestionFromSideBar?: (arg0: any) => any;
    presentation: Presentation;
    onSettingsDirtyChange?: (dirty: boolean) => void;
  }
>(({ addQuestionFromSideBar, presentation, onSettingsDirtyChange }, settingsRef) => {
  const eventContext = useContext(EventContext);
  const [mobEdit, setMobEdit] = useState(false);
  const location = useLocation();
  const [isVirtual] = useState(queryString.parse(location.search).viewmode === "virtual");
  const intl = useIntl();
  const formikRef = useRef<any>();
  const [settingsBaseline, setSettingsBaseline] =
    useState<QuizModeratorSettingsForm | null>(null);
  const toggleEdit = () => {
    setMobEdit(!mobEdit)
  }

  let history = useHistory();

  const { data, loading: queryLoading, refetch } = useQuery(presentation_by_pk, {
    variables: {
      id: presentation && presentation._id
    },
    skip: !presentation
  })


  const notify = () => {

    toast.success("Copied to clipboard", { position: 'top-left' })
  };


  useEffect(() => {
    if (data?.presentation_by_pk) {
      setSettingsBaseline(buildSettingsFormFromPresentation(data.presentation_by_pk));
    }
  }, [data?.presentation_by_pk]);

  useImperativeHandle(
    settingsRef,
    () => ({
      hasUnsavedChanges: () => {
        const formik = formikRef.current;
        if (!formik || !settingsBaseline) {
          return false;
        }
        return hasQuizModeratorSettingsChanges(formik.values, settingsBaseline);
      },
      submitSettings: () => {
        formikRef.current?.submitForm();
      },
    }),
    [settingsBaseline]
  );

  const [update_presentation_by_pk_mutation, { loading: mutationLoading }] = useMutation(update_presentation_by_pk, {
    onCompleted: (response) => {
      const updated = response?.update_presentation_by_pk;
      if (updated) {
        setSettingsBaseline(buildSettingsFormFromPresentation(updated));
      }
      refetch();
      Swal.fire({
        title: 'Updated successfully',
        icon: 'success',
      });
    },
    onError: (error) => {
       console.log("update_presentation_by_pk_mutation", error);
       console.log("TIMER", error);
      // Swal.fire({
      //   title: 'There was an error',
      //   icon: 'error',
      // })
    }
  })


  if (queryLoading || !presentation) {
    return <PreLoader />
  }

  if (!data || !data.presentation_by_pk) {
    return <i>Error while fetching presentation data</i>
  }

  const settingsInitialValues = buildSettingsFormFromPresentation(data.presentation_by_pk);

  return (
    <>
      <div className="sidebar ">
        <div className="sidebar-main quiz-user quiz-moderator moderator-curve">
          <div className="logo-container">
            {/* <img src="/images/logoplaceholder.png" alt="logo" /> */}
            <span className="day">
              {!isVirtual && <span className="day-text">{intl.formatMessage({ id: 'day' })} {presentation.day}</span>}
              <span className="useless-line"></span>
              {!isVirtual && presentation.date && <span className="date">{DateTime.fromFormat(presentation.date, 'yyyy-MM-dd').toFormat('D')}</span>}
            </span>
            <h6>{presentation.name}</h6>
            <div className="speaker-deails-sidebar">
              {
                presentation.speaker_info && presentation.speaker_info.picture ? <img src={getEncodedImageUrl(presentation.speaker_info && presentation.speaker_info.picture, 56, 56)} alt="speaker" /> : <img src="/images/plimgspeaker.png" alt="speaker" />
              }
              <div>
                <p>{presentation.speaker_info && presentation.speaker_info.title} {presentation.speaker_info && presentation.speaker_info.firstName} {presentation.speaker_info && presentation.speaker_info.lastName}</p>
                {
                  !isVirtual &&
                  presentation.time &&
                  presentation.end_time &&
                  <span className="time-room-container">
                    {secondsToTime(presentation.time)} - {secondsToTime(presentation.end_time)}
                    <span className="room-name">{presentation.room_info && presentation.room_info.name}</span>
                  </span>
                }
              </div>
            </div>
            <div className="back-link m-t-30">
              <Button onClick={() => history.push(`/${eventContext.slug}${document.location.search}`)} color="link"><BackArrow />{intl.formatMessage({ id: 'button.common.back' })}</Button>
            </div>
          </div>
          <div className={`nav-conatiner ${mobEdit ? 'show-mob-edit' : ''} mobile-unset-padding`}>
            <>
              <Button onClick={() => toggleEdit()} className="btn-edit btn-curve  m-t-30" color="secondary" type="button">Edit<img src="/icons/down-arrow-white-chev.svg" alt="" /></Button>

              <Formik
                initialValues={settingsInitialValues}
                enableReinitialize
                innerRef={formikRef}
                onSubmit={async (values) => {
                  const presentationRow = data.presentation_by_pk;
                  let questions_from_time: string | null = presentationRow.questions_from_time ?? null;
                  let questions_to_time: string | null = presentationRow.questions_to_time ?? null;
                  let results_from_time: string | null = presentationRow.results_from_time ?? null;
                  let results_to_time: string | null = presentationRow.results_to_time ?? null;

                  if (!values.live) {
                    const questionsFromTime = buildQuizScheduleTimestamp(
                      values.questions_from_date,
                      values.questions_from_time
                    );
                    const questionsToTime = buildQuizScheduleTimestamp(
                      values.questions_to_date,
                      values.questions_to_time
                    );
                    const resultsFromTime = buildQuizScheduleTimestamp(
                      values.results_from_date,
                      values.results_from_time
                    );
                    const resultsToTime = buildQuizScheduleTimestamp(
                      values.results_to_date,
                      values.results_to_time
                    );

                    if (
                      !questionsFromTime ||
                      !questionsToTime ||
                      !resultsFromTime ||
                      !resultsToTime
                    ) {
                      Swal.fire({
                        title: 'Please enter valid schedule dates and times',
                        icon: 'warning',
                      });
                      return;
                    }

                    if (questionsToTime <= questionsFromTime) {
                      Swal.fire({
                        title: 'Questions To time, should be greater than Questions From time',
                        icon: 'warning',
                      })
                      return;
                    }

                    if (resultsToTime <= resultsFromTime) {
                      Swal.fire({
                        title: 'Results To time, should be greater than Results From time',
                        icon: 'warning',
                      })
                      return;
                    }

                    questions_from_time = questionsFromTime.toISO();
                    questions_to_time = questionsToTime.toISO();
                    results_from_time = resultsFromTime.toISO();
                    results_to_time = resultsToTime.toISO();
                  }

                  await update_presentation_by_pk_mutation({
                    variables: {
                      pk_columns: {
                        id: presentation._id
                      },
                      questions_from_time,
                      questions_to_time,
                      results_from_time,
                      results_to_time,
                      live: values.live,
                      timer: values.timer
                    }
                  })
                }}
              >
                {({ values }) => {
                  return (
                    <Form className="sidebar-form mobile_background">
                      <SettingsDirtyTracker
                        values={values}
                        baseline={settingsBaseline}
                        onDirtyChange={onSettingsDirtyChange}
                      />
                      <Row>
                        <Col xs={9}>
                          {
                            !values.live &&
                            <p>{intl.formatMessage({ id: 'quiz.show.questions' })}</p>
                          }
                        </Col>
                        <Col className="text-center" xs={3}>
                          <span className="live-text">LIVE</span>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={9}>
                          {
                            !values.live &&
                            <React.Fragment>
                              <Row>
                                <Col xs={3}>
                                    <span className="label-text from">{intl.formatMessage({id:'quiz.from'})}</span>
                                </Col>
                                <Col>
                                  <div className="date-time-picker-container">
                                    <Field
                                      name="questions_from_date"
                                      component={ReactDatePicker}
                                      placeholder='00/00/00'
                                      required
                                      clasName="form-control"
                                    />
                                    <Field
                                      component={TimePicker}
                                      name="questions_from_time"
                                      id="questions_from_time"
                                      label={"0h00"}
                                      placeholder="0h00"
                                      changeText={"Done"}
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col xs={3}>
                                    <span className="label-text from">{intl.formatMessage({id:'quiz.to'})}</span>
                                </Col>
                                <Col>
                                  <div className="date-time-picker-container">
                                    <Field
                                      name="questions_to_date"
                                      component={ReactDatePicker}
                                      placeholder='00/00/00'
                                      required
                                      // disabled={!values.questions_from_date}
                                      clasName="form-control"
                                    />
                                    <Field
                                      component={TimePicker}
                                      name="questions_to_time"
                                      id="questions_to_time"
                                      label={"0h00"}
                                      placeholder="0h00"
                                      changeText={"Done"}
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <p className="m-t-20 m-b-20">{intl.formatMessage({ id: 'quiz.show.results' })}</p>
                              <Row>
                                <Col xs={3}>
                                  <span className="label-text from">{intl.formatMessage({id:'quiz.from'})}</span>
                                </Col>
                                <Col>
                                  <div className="date-time-picker-container">
                                    <Field
                                      name="results_from_date"
                                      component={ReactDatePicker}
                                      placeholder='00/00/00'
                                      // required
                                      clasName="form-control"
                                    />
                                    <Field
                                      component={TimePicker}
                                      name="results_from_time"
                                      id="results_from_time"
                                      label={"0h00"}
                                      placeholder="0h00"
                                      changeText={"Done"}
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col xs={3}>
                                    <span className="label-text from">{intl.formatMessage({id:'quiz.to'})}</span>
                                </Col>
                                <Col>
                                  <div className="date-time-picker-container">
                                    <Field
                                      name="results_to_date"
                                      component={ReactDatePicker}
                                      placeholder='00/00/00'
                                      // required
                                      clasName="form-control"
                                    />
                                    <Field
                                      component={TimePicker}
                                      name="results_to_time"
                                      id="results_to_time"
                                      label={"0h00"}
                                      placeholder="0h00"
                                      changeText={"Done"}
                                    />
                                  </div>
                                </Col>
                              </Row>
                            </React.Fragment>
                          }
                          {values.live &&
                            <div className="d-none d-lg-block">
                              <Row>
                                <Col>
                                  <div className={`quiz-timer ${values.live ? 'live-space' : ''}`}>
                                    <p className="m-t-20 m-b-10">Quiz timer</p>
                                    <Field
                                      type="number"
                                      name="timer"
                                      required
                                      component={Input}
                                      placeholder="00s"
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <a className="link-btn m-t-30" href={`/${eventContext.slug}/${presentation._id}/quiz-user-list`}>SPEAKER MODE<img src="/icons/arrow-right-link.svg" alt="" /></a>
                              <div style={{ cursor: 'pointer' }} onClick={() => {
                                notify()
                                navigator.clipboard.writeText(window.location.origin + `/${eventContext.slug}/${presentation._id}/quiz-user-list`)
                              }}> <span className="url-text"><img src="/icons/link-ico.svg" alt="" />Url for Speaker mode</span></div>


                              <Link className="link-btn m-t-30" target="_blank" to={`/${eventContext.slug}/${presentation._id}/quiz-user-list`} >SCREEN MODE<img src="/icons/arrow-right-link.svg" alt="" /></Link>
                              <div style={{ cursor: 'pointer' }} onClick={() => {
                                notify()
                                navigator.clipboard.writeText(window.location.origin + `/${eventContext.slug}/${presentation._id}/screen-user-list`)
                              }}> <span className="url-text"><img src="/icons/link-ico.svg" alt="" />Url for Screen Mode</span></div>
                              <Toaster />
                            </div>}

                        </Col>
                        <Col xs={3}>
                          <div className="m-t-30">
                            <Field
                              component={ReactSwitch}
                              name="live"
                              id="live"
                              disabled={false}
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="d-block d-lg-none">
                        <a className="link-btn m-t-30" href={`/${eventContext.slug}/quiz-user-list`}>SPEAKER MODE<img src="/icons/arrow-right-link.svg" alt="" /></a>
                        <div style={{ cursor: 'pointer' }} onClick={() => {
                          navigator.clipboard.writeText(window.location.origin)
                        }}> <span className="url-text"><img src="/icons/link-ico.svg" alt="" />Url for Speaker mode</span></div>
                        <Link className="link-btn m-t-30" to="/">SCREEN MODE<img src="/icons/arrow-right-link.svg" alt="" /></Link>
                        <span className="url-text"><img src="/icons/link-ico.svg" alt="" />Url for Screen Mode</span>
                      </div>

                      <Button className="btn-submit m-t-30 btn-hide" color="primary" type="submit" disabled={mutationLoading}>{intl.formatMessage({ id: 'save' })}</Button>
                      <Button onClick={(e) => addQuestionFromSideBar && addQuestionFromSideBar(e)} className="btn-submit m-t-30" color="primary" type="button"><img src="/icons/add-ico.svg" alt="" />{intl.formatMessage({ id: 'quiz.add.question' })}</Button>
                    </Form>
                  )
                }}
              </Formik>
            </>
          </div>
        </div>
      </div>
    </>
  )
});

SidebarQuizModerator.displayName = 'SidebarQuizModerator';

export default SidebarQuizModerator;
